import React, { FormEvent, RefObject } from 'react';
import styles from './VideoSearchBar.module.scss';

interface VideoSearchBarProps {
    value: string;
    onValueChange: (value: string) => void;
    onSubmit: () => void;
    onClear?: () => void;
    inputId?: string;
    inputRef?: RefObject<HTMLInputElement>;
    inputAriaLabel?: string;
    placeholder?: string;
    className?: string;
    disableSubmitWhenEmpty?: boolean;
    showClearButton?: boolean;
}

/**
 * Shared public search input used by search surfaces without coupling it to discovery panels.
 */
const VideoSearchBar = ({
    value,
    onValueChange,
    onSubmit,
    onClear,
    inputId = 'video-search-input',
    inputRef,
    inputAriaLabel = 'Search videos',
    placeholder = 'Search fights, fighters, or events',
    className,
    disableSubmitWhenEmpty = false,
    showClearButton = false
}: VideoSearchBarProps) => {
    const trimmedValue = value.trim();
    const canSubmit = !disableSubmitWhenEmpty || trimmedValue.length > 0;
    const barClassName = [
        styles.bar,
        className ?? ''
    ].filter(Boolean).join(' ');

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }

        onSubmit();
    };

    return (
        <form className={barClassName} onSubmit={handleSubmit}>
            <div className={styles.inputWrap}>
                <span className={`material-symbols-outlined ${styles.searchIcon}`} aria-hidden="true">search</span>
                <input
                    id={inputId}
                    ref={inputRef}
                    className={styles.input}
                    type="search"
                    value={value}
                    onChange={(event) => onValueChange(event.target.value)}
                    placeholder={placeholder}
                    autoComplete="off"
                    aria-label={inputAriaLabel}
                />
                {showClearButton && trimmedValue ? (
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={() => {
                            if (onClear) {
                                onClear();
                                return;
                            }
                            onValueChange('');
                        }}
                        aria-label="Clear search"
                    >
                        <span className="material-symbols-outlined" aria-hidden="true">close</span>
                    </button>
                ) : null}
            </div>
            <button type="submit" className={styles.submitButton} disabled={!canSubmit}>
                Search
            </button>
        </form>
    );
};

export default VideoSearchBar;
